import { type ComponentType, type SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@tg-hookah/ui';

export type NeonGlow =
  | 'cyan'
  | 'magenta'
  | 'violet'
  | 'amber'
  | 'emerald'
  | 'rose';

/** Muted hue per glow — each tile carries one low-saturation accent so the
 *  grid has variety without becoming neon. Intensity is dialled down in
 *  alpha values applied in the style below, not in the hue itself. */
const GLOW_MAP: Record<NeonGlow, { rgb: string; hex: string }> = {
  cyan: { rgb: '79, 185, 194', hex: '#4fb9c2' },
  magenta: { rgb: '180, 99, 184', hex: '#b463b8' },
  violet: { rgb: '138, 125, 196', hex: '#8a7dc4' },
  amber: { rgb: '210, 164, 84', hex: '#d2a454' },
  emerald: { rgb: '110, 169, 135', hex: '#6ea987' },
  rose: { rgb: '197, 116, 128', hex: '#c57480' },
};

export type LucideIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

interface BaseProps {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  glow: NeonGlow;
  /** Emphasised tile — taller with a stronger edge. */
  featured?: boolean;
  /** Span two columns on a 2-col grid. */
  wide?: boolean;
  /** Optional background photograph; rendered at 45% opacity under a scrim. */
  image?: string | null;
  delay?: number;
  className?: string;
  /** Analytics label emitted on click (via the global `data-track` listener). */
  track?: string;
}

type Props =
  | (BaseProps & { href: string; onClick?: never })
  | (BaseProps & { onClick: () => void; href?: never });

/**
 * Graphite tile with an editorial voice — hairline frame, white display
 * type, and a single muted hue as a thin top-edge line plus a soft corner
 * bloom. The colour is intentional but low-saturation: the surface still
 * reads mostly black-and-white.
 */
export function NeonTile(props: Props) {
  const {
    title,
    subtitle,
    icon: Icon,
    glow,
    featured,
    wide,
    image,
    delay = 0,
    className,
    track,
  } = props;
  const { rgb } = GLOW_MAP[glow];
  const topAlpha = featured ? 0.5 : 0.38;
  const bloomAlpha = featured ? 0.3 : 0.22;

  const content = (
    <>
      {/* Optional background photograph, rendered under a dark scrim so the
          title and icon badge stay legible regardless of the image. */}
      {image ? (
        <>
          <img
            src={image}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-55"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(10,10,11,0.15) 0%, rgba(10,10,11,0.72) 100%)',
            }}
          />
        </>
      ) : null}

      {/* Coloured top-edge hairline — the tile's signature accent. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent 8%, rgba(${rgb}, ${topAlpha}) 50%, transparent 92%)`,
        }}
      />

      {/* Corner bloom — soft, low-saturation. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-14 -left-14 w-44 h-44 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(closest-side, rgba(${rgb}, ${bloomAlpha}), transparent 72%)`,
        }}
      />

      {/* Inner gradient for depth. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.035] via-transparent to-black/20"
      />

      {/* Icon — flex child, sits at the top. White-tinted badge to match
          the Hero tiles (Бронь / Меню) — the colour identity of the tile is
          carried by the top-edge hairline and corner bloom only. */}
      <span
        className="relative inline-flex w-9 h-9 items-center justify-center rounded-xl shrink-0"
        style={{
          border: '1px solid var(--line-2)',
          background: 'rgba(255,255,255,0.03)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
        }}
      >
        <Icon size={18} strokeWidth={1.5} className="text-white/90" />
      </span>

      {/* Text block — mt-auto pushes it to the tile's bottom edge regardless
          of whether the parent's height is definite or min-height. This is
          what justify-between failed to do reliably on iOS Safari. */}
      <div className="relative mt-auto">
        <div
          className={cn(
            'font-display leading-[1.08] tracking-tight text-white',
            featured ? 'text-xl font-extrabold' : 'text-[15px] font-bold',
          )}
        >
          {title}
        </div>
        {subtitle ? (
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-white/45">
            {subtitle}
          </div>
        ) : null}
      </div>

      {/* Bottom coloured hairline — paired with the top one. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-4 bottom-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, rgba(${rgb}, ${topAlpha * 0.55}), transparent)`,
        }}
      />
    </>
  );

  const rootClass = cn(
    'group relative isolate overflow-hidden rounded-2xl text-left',
    'flex flex-col p-4',
    'bg-[var(--ink-1)]',
    'border border-[var(--line-1)]',
    'transition-all duration-300 ease-out',
    'active:scale-[0.98] hover:border-[var(--line-2)]',
    'shadow-[0_10px_28px_-18px_rgba(0,0,0,0.75)]',
    featured ? 'min-h-[180px]' : 'min-h-[128px]',
    wide && 'col-span-2',
    'rise-in',
    className,
  );

  const style = {
    animationDelay: `${delay}ms`,
  } as const;

  if ('href' in props && props.href) {
    return (
      <Link to={props.href} className={rootClass} style={style} data-track={track}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      className={rootClass}
      style={style}
      data-track={track}
    >
      {content}
    </button>
  );
}
